import { useEffect, useState, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { 
  MapPin, 
  AlertTriangle, 
  Loader2, 
  RefreshCw,
  Building2,
  Maximize2,
  Minimize2,
  Sparkles,
  X,
  Target,
  Plus,
  Pencil,
  Eraser,
  MousePointer
} from "lucide-react";
import { MapContainer, TileLayer, Marker, Popup, Tooltip, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { toast } from "sonner";

// Fix for default marker icon in React-Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

// Custom marker icons by asset tier
const createTierIcon = (tier, isHighlighted = false) => {
  const colors = {
    tier_1: "#EF4444", // Red - Critical
    tier_2: "#F59E0B", // Amber - Important
    tier_3: "#10B981", // Green - Standard
    default: "#6366F1" // Indigo
  };
  const color = colors[tier] || colors.default;
  const size = isHighlighted ? 32 : 24;
  const borderWidth = isHighlighted ? 4 : 3;
  
  return L.divIcon({
    className: 'custom-marker',
    html: `<div style="
      background-color: ${color}; 
      width: ${size}px; 
      height: ${size}px; 
      border-radius: 50%; 
      border: ${borderWidth}px solid white; 
      box-shadow: 0 2px 8px rgba(0,0,0,${isHighlighted ? 0.5 : 0.3});
      ${isHighlighted ? 'animation: pulse 1.5s infinite;' : ''}
    "></div>`,
    iconSize: [size, size],
    iconAnchor: [size/2, size/2],
  });
};

// Location type colors
const LOCATION_TYPE_COLORS = {
  headquarters: "#3B82F6",
  branch: "#10B981",
  datacenter: "#8B5CF6",
  warehouse: "#F59E0B",
  backoffice: "#F97316",
  manufacturing: "#84CC16",
  retail: "#14B8A6",
  other: "#6B7280",
  default: "#6366F1"
};

// Custom Drawing Control - Using lat/lng as single source of truth
function FreehandDrawControl({ onRegionSelected, onClearSelection, drawMode, setDrawMode, clearTrigger }) {
  const map = useMap();
  const [drawingLatLngs, setDrawingLatLngs] = useState([]);  // Lat/lng points while actively drawing
  const [completedLatLngs, setCompletedLatLngs] = useState(null);  // Lat/lng points for completed polygon
  const isDrawingRef = useRef(false);
  const drawModeRef = useRef(drawMode);
  const drawingLatLngsRef = useRef([]);  // Keep a ref copy to avoid stale closure issues
  
  // Keep refs in sync
  useEffect(() => {
    drawModeRef.current = drawMode;
  }, [drawMode]);
  
  useEffect(() => {
    drawingLatLngsRef.current = drawingLatLngs;
  }, [drawingLatLngs]);
  
  // Clear polygon when clearTrigger changes (increments)
  useEffect(() => {
    if (clearTrigger > 0) {
      setCompletedLatLngs(null);
      setDrawingLatLngs([]);
      drawingLatLngsRef.current = [];
    }
  }, [clearTrigger]);
  
  // Convert lat/lng array to screen coordinates for SVG rendering
  const latLngToScreenPoints = useCallback((latLngs) => {
    if (!latLngs || latLngs.length === 0) return [];
    return latLngs.map(ll => {
      const point = map.latLngToContainerPoint([ll.lat, ll.lng]);
      return { x: point.x, y: point.y };
    });
  }, [map]);
  
  // Setup event handlers
  useEffect(() => {
    const container = map.getContainer();
    
    const handleMouseDown = (e) => {
      if (drawModeRef.current !== 'draw') return;
      
      e.preventDefault();
      e.stopPropagation();
      
      isDrawingRef.current = true;
      
      // Get container-relative coordinates
      const rect = container.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      
      // Convert to lat/lng using Leaflet
      const containerPoint = L.point(x, y);
      const latlng = map.containerPointToLatLng(containerPoint);
      
      // Store as lat/lng - this is our source of truth
      const newPoint = { lat: latlng.lat, lng: latlng.lng };
      drawingLatLngsRef.current = [newPoint];
      setDrawingLatLngs([newPoint]);
    };
    
    const handleMouseMove = (e) => {
      if (!isDrawingRef.current) return;
      if (drawModeRef.current !== 'draw') return;
      
      // Convert screen position to lat/lng
      const rect = container.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      const point = L.point(x, y);
      const latlng = map.containerPointToLatLng(point);
      
      // Add to lat/lng points - use ref to avoid stale state
      const newPoint = { lat: latlng.lat, lng: latlng.lng };
      drawingLatLngsRef.current = [...drawingLatLngsRef.current, newPoint];
      setDrawingLatLngs([...drawingLatLngsRef.current]);
    };
    
    const handleMouseUp = () => {
      if (!isDrawingRef.current) return;
      
      isDrawingRef.current = false;
      
      // Get current drawing points from ref (avoids stale closure)
      const currentLatLngs = drawingLatLngsRef.current;
      
      // Need at least 3 points to form a polygon
      if (currentLatLngs.length >= 3) {
        // Close the polygon - add first point to end
        const closedLatLngs = [...currentLatLngs, currentLatLngs[0]];
        
        // Save for persistent display
        setCompletedLatLngs(closedLatLngs);
        
        // Pass lat/lng coordinates to parent for filtering
        const bounds = {
          type: 'polygon',
          coordinates: closedLatLngs.map(ll => [ll.lat, ll.lng])
        };
        
        // Use setTimeout to avoid calling parent setState during render
        setTimeout(() => {
          onRegionSelected(bounds);
          setDrawMode('view');
        }, 0);
      } else {
        toast.error("Draw a larger shape - drag further across the map");
      }
      
      // Clear active drawing (completed polygon remains)
      drawingLatLngsRef.current = [];
      setDrawingLatLngs([]);
    };
    
    const handleMouseLeave = () => {
      if (isDrawingRef.current) {
        // Try to complete if we have enough points
        handleMouseUp();
      }
    };
    
    // Attach event listeners
    container.addEventListener('mousedown', handleMouseDown);
    container.addEventListener('mousemove', handleMouseMove);
    container.addEventListener('mouseup', handleMouseUp);
    container.addEventListener('mouseleave', handleMouseLeave);
    
    return () => {
      container.removeEventListener('mousedown', handleMouseDown);
      container.removeEventListener('mousemove', handleMouseMove);
      container.removeEventListener('mouseup', handleMouseUp);
      container.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [map, onRegionSelected, setDrawMode]);
  
  // Handle draw mode changes
  useEffect(() => {
    const container = map.getContainer();
    if (drawMode === 'draw') {
      map.dragging.disable();
      map.doubleClickZoom.disable();
      map.scrollWheelZoom.disable();
      container.style.cursor = 'crosshair';
    } else {
      map.dragging.enable();
      map.doubleClickZoom.enable();
      map.scrollWheelZoom.enable();
      container.style.cursor = '';
    }
  }, [drawMode, map]);
  
  // Convert lat/lng to screen coordinates for rendering
  const drawingScreenPoints = latLngToScreenPoints(drawingLatLngs);
  const completedScreenPoints = latLngToScreenPoints(completedLatLngs);
  
  // Generate SVG path for active drawing line
  const drawingPath = drawingScreenPoints.length > 0
    ? drawingScreenPoints.map((p, i) => (i === 0 ? `M ${p.x} ${p.y}` : `L ${p.x} ${p.y}`)).join(' ')
    : '';
  
  // Generate SVG path for completed polygon (closed shape with fill)
  const polygonPath = completedScreenPoints.length > 0
    ? completedScreenPoints.map((p, i) => (i === 0 ? `M ${p.x} ${p.y}` : `L ${p.x} ${p.y}`)).join(' ') + ' Z'
    : '';
  
  // Render SVG overlay
  const shouldRender = drawingLatLngs.length > 0 || completedLatLngs;
  
  return shouldRender ? createPortal(
    <svg
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 10000,
      }}
    >
      {/* Completed polygon - stays visible until Clear */}
      {completedLatLngs && (
        <path
          d={polygonPath}
          fill="rgba(239, 68, 68, 0.2)"
          stroke="#ef4444"
          strokeWidth="4"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      )}
      {/* Active drawing line */}
      {drawingLatLngs.length > 0 && (
        <path
          d={drawingPath}
          fill="none"
          stroke="#ef4444"
          strokeWidth="5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      )}
    </svg>,
    map.getContainer()
  ) : null;
}

// Map center updater
function MapCenterUpdater({ center, zoom }) {
  const map = useMap();
  useEffect(() => {
    if (center) {
      map.setView(center, zoom || 10);
    }
  }, [center, zoom, map]);
  return null;
}

// Check if point is inside polygon using ray casting algorithm
// Note: In geographic coordinates, lat=Y (north-south), lng=X (east-west)
function isPointInPolygon(point, polygon) {
  if (!point || !polygon || polygon.length < 3) return false;
  
  // point and polygon vertices are [lat, lng] format
  // For ray casting: x = longitude (east-west), y = latitude (north-south)
  const x = point[1]; // longitude is X
  const y = point[0]; // latitude is Y
  let inside = false;
  
  const n = polygon.length;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const xi = polygon[i][1]; // longitude
    const yi = polygon[i][0]; // latitude
    const xj = polygon[j][1]; // longitude
    const yj = polygon[j][0]; // latitude
    
    // Ray casting: check if horizontal ray from point crosses edge
    if (((yi > y) !== (yj > y)) && (x < (xj - xi) * (y - yi) / (yj - yi) + xi)) {
      inside = !inside;
    }
  }
  
  return inside;
}

// Check if point is inside circle
function isPointInCircle(point, center, radius) {
  const [lat1, lng1] = point;
  const [lat2, lng2] = center;
  
  const R = 6371000; // Earth radius in meters
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
            Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
            Math.sin(dLng/2) * Math.sin(dLng/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  const distance = R * c;
  
  return distance <= radius;
}

export default function AssetInventoryMap({ 
  assets = [], 
  locations = [],
  onAssetSelect,
  onFilteredAssetsChange,
  isExpanded = false,
  onToggleExpand
}) {
  const { api } = useAuth();
  const [loading, setLoading] = useState(false);
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [filteredAssetIds, setFilteredAssetIds] = useState(new Set());
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [showAiPanel, setShowAiPanel] = useState(false);
  const [analyzingDensity, setAnalyzingDensity] = useState(false);
  const [demoMode, setDemoMode] = useState(false);
  const [seedingData, setSeedingData] = useState(false);
  const [drawMode, setDrawMode] = useState('view'); // 'view', 'draw', 'edit', 'delete'
  const [clearTrigger, setClearTrigger] = useState(0); // Increment to clear polygon
  
  // Default center (US)
  const defaultCenter = [39.8283, -98.5795];
  const [mapCenter, setMapCenter] = useState(defaultCenter);

  // Demo assets for visualization when no real data exists
  const DEMO_ASSETS = [
    { id: 'demo-1', name: 'Core Banking System', asset_type: 'application', criticality_tier: 'tier_1', coords: [40.7484, -73.9857], locationName: 'NYC HQ', rto_hours: 2 },
    { id: 'demo-2', name: 'Customer Database', asset_type: 'database', criticality_tier: 'tier_1', coords: [37.4847, -122.1477], locationName: 'West Coast DC', rto_hours: 1 },
    { id: 'demo-3', name: 'HR Management System', asset_type: 'application', criticality_tier: 'tier_2', coords: [41.8789, -87.6359], locationName: 'Chicago Hub', rto_hours: 8 },
    { id: 'demo-4', name: 'Manufacturing Control', asset_type: 'application', criticality_tier: 'tier_1', coords: [30.4861, -97.6725], locationName: 'TX Plant', rto_hours: 4 },
    { id: 'demo-5', name: 'CRM Platform', asset_type: 'application', criticality_tier: 'tier_2', coords: [25.7617, -80.1918], locationName: 'Miami Office', rto_hours: 12 },
    { id: 'demo-6', name: 'Development Servers', asset_type: 'server', criticality_tier: 'tier_3', coords: [47.6205, -122.3493], locationName: 'Seattle Campus', rto_hours: 24 },
    { id: 'demo-7', name: 'Backup Systems', asset_type: 'server', criticality_tier: 'tier_2', coords: [39.7510, -104.9997], locationName: 'Denver Backup', rto_hours: 4 },
    { id: 'demo-8', name: 'Research Data Lake', asset_type: 'database', criticality_tier: 'tier_2', coords: [42.3588, -71.0556], locationName: 'Boston Lab', rto_hours: 8 },
    { id: 'demo-9', name: 'Email Servers', asset_type: 'server', criticality_tier: 'tier_2', coords: [40.7580, -73.9855], locationName: 'NYC HQ', rto_hours: 2 },
    { id: 'demo-10', name: 'Financial Analytics', asset_type: 'application', criticality_tier: 'tier_1', coords: [40.7420, -73.9900], locationName: 'NYC HQ', rto_hours: 1 },
    { id: 'demo-11', name: 'Supply Chain System', asset_type: 'application', criticality_tier: 'tier_2', coords: [30.4900, -97.6800], locationName: 'TX Plant', rto_hours: 6 },
    { id: 'demo-12', name: 'DR Site Infrastructure', asset_type: 'server', criticality_tier: 'tier_1', coords: [39.7600, -105.0100], locationName: 'Denver Backup', rto_hours: 1 },
  ];
  
  // Combine assets with location data
  const assetsWithCoords = assets.filter(asset => {
    // Asset has direct coordinates
    if (asset.latitude && asset.longitude) return true;
    // Asset has linked location
    if (asset.location_id) {
      const location = locations.find(l => l.id === asset.location_id);
      return (location?.coordinates?.lat && location?.coordinates?.lng) || (location?.latitude && location?.longitude);
    }
    return false;
  }).map(asset => {
    if (asset.latitude && asset.longitude) {
      return { ...asset, coords: [asset.latitude, asset.longitude] };
    }
    const location = locations.find(l => l.id === asset.location_id);
    const lat = location?.coordinates?.lat || location?.latitude;
    const lng = location?.coordinates?.lng || location?.longitude;
    return { 
      ...asset, 
      coords: [lat, lng],
      locationName: location.name
    };
  });

  // Use demo assets if no real data and demo mode is on
  const displayAssets = demoMode && assetsWithCoords.length === 0 ? DEMO_ASSETS : assetsWithCoords;

  // Seed demo data function
  const seedDemoData = async () => {
    setSeedingData(true);
    try {
      // First seed locations
      await api.post("/bcm-admin/seed-demo-locations");
      // Then seed assets
      await api.post("/bcm-admin/seed-demo-assets");
      toast.success("Demo data seeded! Refreshing...");
      window.location.reload();
    } catch (error) {
      console.error("Failed to seed demo data:", error);
      toast.error("Failed to seed demo data. Try enabling Demo Mode instead.");
    } finally {
      setSeedingData(false);
    }
  };
  
  // Handle region selection
  const handleRegionSelected = useCallback((bounds) => {
    setSelectedRegion(bounds);
    
    // Filter assets within the region
    const filtered = new Set();
    displayAssets.forEach(asset => {
      let isInside = false;
      
      if (bounds.type === 'circle') {
        isInside = isPointInCircle(asset.coords, bounds.center, bounds.radius);
      } else if (bounds.type === 'polygon') {
        isInside = isPointInPolygon(asset.coords, bounds.coordinates);
      }
      
      if (isInside) {
        filtered.add(asset.id);
      }
    });
    
    setFilteredAssetIds(filtered);
    if (onFilteredAssetsChange && !demoMode) {
      const filteredAssets = assets.filter(a => filtered.has(a.id));
      onFilteredAssetsChange(filteredAssets);
    }
    
    toast.success(`${filtered.size} assets found in selected region`);
  }, [displayAssets, assets, onFilteredAssetsChange, demoMode]);
  
  const handleClearSelection = useCallback(() => {
    setSelectedRegion(null);
    setFilteredAssetIds(new Set());
    if (onFilteredAssetsChange) {
      onFilteredAssetsChange(null);
    }
    setAiAnalysis(null);
    setShowAiPanel(false);
    setClearTrigger(prev => prev + 1);  // Trigger polygon clear
  }, [onFilteredAssetsChange]);
  
  // AI Density Analysis
  const analyzeAssetDensity = async () => {
    if (displayAssets.length === 0) {
      toast.error("No assets with location data to analyze. Enable Demo Mode to see sample data.");
      return;
    }
    
    setAnalyzingDensity(true);
    try {
      const response = await api.post("/ai-assistant/analyze-asset-density", {
        assets: displayAssets.map(a => ({
          id: a.id,
          name: a.name || a.asset_name,
          type: a.asset_type,
          tier: a.criticality_tier,
          coords: a.coords,
          location: a.locationName
        })),
        locations: locations
      });
      
      setAiAnalysis(response.data);
      setShowAiPanel(true);
      toast.success("AI density analysis complete");
    } catch (error) {
      console.error("Failed to analyze asset density:", error);
      // Honest failure: do not fabricate hotspots/clusters/recommendations.
      setAiAnalysis(null);
      setShowAiPanel(false);
      toast.error("AI density analysis failed — no analysis was generated. Please retry.");
    } finally {
      setAnalyzingDensity(false);
    }
  };
  
  // Calculate initial center from assets
  useEffect(() => {
    if (displayAssets.length > 0) {
      const avgLat = displayAssets.reduce((sum, a) => sum + a.coords[0], 0) / displayAssets.length;
      const avgLng = displayAssets.reduce((sum, a) => sum + a.coords[1], 0) / displayAssets.length;
      setMapCenter([avgLat, avgLng]);
    } else if (locations.length > 0) {
      const validLocs = locations.filter(l => (l.coordinates?.lat && l.coordinates?.lng) || (l.latitude && l.longitude));
      if (validLocs.length > 0) {
        const avgLat = validLocs.reduce((sum, l) => sum + (l.coordinates?.lat || l.latitude), 0) / validLocs.length;
        const avgLng = validLocs.reduce((sum, l) => sum + (l.coordinates?.lng || l.longitude), 0) / validLocs.length;
        setMapCenter([avgLat, avgLng]);
      }
    }
  }, [displayAssets, locations, demoMode]);

  const mapHeight = isExpanded ? "h-[600px]" : "h-[400px]";

  return (
    <Card className={`${isExpanded ? 'col-span-full' : ''}`}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <MapPin className="w-5 h-5 text-sky-700" />
            <CardTitle className="text-lg">Asset Geographic Intelligence</CardTitle>
            <Badge variant="outline" className={`ml-2 ${demoMode ? 'bg-amber-50 border-amber-300 text-amber-700' : ''}`}>
              {displayAssets.length} {demoMode ? 'demo' : 'mapped'} assets
            </Badge>
            {demoMode && (
              <Badge variant="outline" className="bg-amber-100 text-amber-700 border-amber-300">
                Demo Mode
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {assetsWithCoords.length === 0 && (
              <>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setDemoMode(!demoMode)}
                  className={demoMode ? 'bg-amber-50 border-amber-300' : ''}
                >
                  {demoMode ? 'Exit Demo' : 'Enable Demo Mode'}
                </Button>
                <Button 
                  variant="default" 
                  size="sm"
                  onClick={seedDemoData}
                  disabled={seedingData}
                  className="bg-sky-700 hover:bg-sky-800"
                >
                  {seedingData ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Plus className="w-4 h-4 mr-2" />
                  )}
                  Seed Real Data
                </Button>
              </>
            )}
            <Button 
              variant="outline" 
              size="sm"
              onClick={analyzeAssetDensity}
              disabled={analyzingDensity || displayAssets.length === 0}
            >
              {analyzingDensity ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Sparkles className="w-4 h-4 mr-2 text-sky-700" />
              )}
              AI Density Analysis
            </Button>
            {selectedRegion && (
              <Button variant="outline" size="sm" onClick={handleClearSelection}>
                <X className="w-4 h-4 mr-1" />
                Clear Selection ({filteredAssetIds.size})
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={onToggleExpand}>
              {isExpanded ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
            </Button>
          </div>
        </div>
        
        {/* Zillow-Style Drawing Toolbar */}
        <div className="flex items-center gap-2 mt-3 p-2 bg-slate-50 rounded-lg border">
          <span className="text-xs font-medium text-slate-600 mr-2">Draw Region:</span>
          <div className="flex items-center gap-1 bg-white rounded-lg border p-1">
            <Button
              variant={drawMode === 'view' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setDrawMode('view')}
              className={`h-8 px-3 ${drawMode === 'view' ? 'bg-slate-800 text-white' : ''}`}
              title="View Mode - Pan & Zoom"
            >
              <MousePointer className="w-4 h-4 mr-1" />
              View
            </Button>
            <Button
              variant={drawMode === 'draw' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setDrawMode('draw')}
              className={`h-8 px-3 ${drawMode === 'draw' ? 'bg-sky-700 text-white' : ''}`}
              title="Draw Mode - Freehand draw a region"
            >
              <Pencil className="w-4 h-4 mr-1" />
              Draw
            </Button>
            <Button
              variant={drawMode === 'edit' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setDrawMode('edit')}
              className={`h-8 px-3 ${drawMode === 'edit' ? 'bg-amber-600 text-white' : ''}`}
              title="Edit Mode - Adjust drawn region"
              disabled={!selectedRegion}
            >
              <Pencil className="w-4 h-4 mr-1" />
              Edit
            </Button>
            <Button
              variant={drawMode === 'delete' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => {
                setDrawMode('delete');
                handleClearSelection();
                setDrawMode('view');
              }}
              className={`h-8 px-3 ${drawMode === 'delete' ? 'bg-red-600 text-white' : ''}`}
              title="Clear drawn region"
              disabled={!selectedRegion}
            >
              <Eraser className="w-4 h-4 mr-1" />
              Clear
            </Button>
          </div>
          {drawMode === 'draw' && (
            <span className="text-xs text-sky-700 ml-2 animate-pulse">
              ✏️ Click and drag on the map to draw a region...
            </span>
          )}
        </div>
        
        <p className="text-sm text-slate-500 mt-2">
          {displayAssets.length === 0 
            ? "No assets with location data. Click 'Seed Real Data' or 'Enable Demo Mode' to see the Zillow-style selection."
            : "Click 'Draw' then drag on the map to draw a freehand region like Zillow. Assets in the region will be highlighted."
          }
        </p>
      </CardHeader>
      <CardContent>
        <div className="flex gap-4">
          <div className={`flex-1 ${mapHeight} rounded-lg overflow-hidden border relative`}>
            {loading && (
              <div className="absolute inset-0 bg-white/80 flex items-center justify-center z-[1000]">
                <Loader2 className="w-8 h-8 animate-spin text-sky-700" />
              </div>
            )}
            
            {/* Draw mode indicator overlay */}
            {drawMode === 'draw' && (
              <div className="absolute top-2 left-2 z-[1000] bg-sky-700 text-white px-3 py-1.5 rounded-lg shadow-lg flex items-center gap-2 text-sm">
                <Pencil className="w-4 h-4" />
                Drawing Mode Active - Drag to draw
              </div>
            )}
            
            <MapContainer
              center={mapCenter}
              zoom={4}
              className={`w-full h-full ${drawMode === 'draw' ? 'cursor-crosshair' : ''}`}
              scrollWheelZoom={drawMode === 'view'}
              dragging={drawMode === 'view'}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <MapCenterUpdater center={mapCenter} zoom={displayAssets.length > 0 ? 4 : 4} />
              <FreehandDrawControl 
                onRegionSelected={handleRegionSelected}
                onClearSelection={handleClearSelection}
                drawMode={drawMode}
                setDrawMode={setDrawMode}
                clearTrigger={clearTrigger}
              />
              
              {/* Asset markers */}
              {displayAssets.map((asset) => {
                const isHighlighted = filteredAssetIds.has(asset.id);
                const tierLabel = asset.criticality_tier === 'tier_1' ? 'Critical' :
                                  asset.criticality_tier === 'tier_2' ? 'Important' : 'Standard';
                return (
                  <Marker
                    key={asset.id}
                    position={asset.coords}
                    icon={createTierIcon(asset.criticality_tier, isHighlighted)}
                    eventHandlers={{
                      click: () => onAssetSelect?.(asset)
                    }}
                  >
                    <Tooltip 
                      direction="top" 
                      offset={[0, -14]} 
                      opacity={0.95}
                      className="asset-tooltip"
                    >
                      <div style={{ minWidth: '180px', padding: '2px' }}>
                        <div style={{ fontWeight: 600, fontSize: '13px', marginBottom: '3px' }}>{asset.name || asset.asset_name}</div>
                        <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '4px' }}>{asset.asset_type} {asset.locationName ? `— ${asset.locationName}` : asset.location ? `— ${asset.location}` : ''}</div>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', fontSize: '11px' }}>
                          <span style={{ 
                            padding: '1px 8px', 
                            borderRadius: '9999px', 
                            fontWeight: 600,
                            background: asset.criticality_tier === 'tier_1' ? '#FEE2E2' : asset.criticality_tier === 'tier_2' ? '#FEF3C7' : '#D1FAE5',
                            color: asset.criticality_tier === 'tier_1' ? '#DC2626' : asset.criticality_tier === 'tier_2' ? '#D97706' : '#059669'
                          }}>{tierLabel}</span>
                          {asset.rto_hours != null && <span style={{ color: '#64748b' }}>RTO: {asset.rto_hours}h</span>}
                          {asset.rpo_hours != null && <span style={{ color: '#64748b' }}>RPO: {asset.rpo_hours}h</span>}
                        </div>
                        {asset.owner && <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '3px' }}>Owner: {asset.owner}</div>}
                      </div>
                    </Tooltip>
                    <Popup>
                      <div className="min-w-[200px]">
                        <h4 className="font-semibold text-sm">{asset.name || asset.asset_name}</h4>
                        <p className="text-xs text-slate-500">{asset.asset_type}</p>
                        {asset.locationName && (
                          <p className="text-xs text-slate-600 mt-1">
                            <MapPin className="w-3 h-3 inline mr-1" />
                            {asset.locationName}
                          </p>
                        )}
                        {!asset.locationName && asset.location && (
                          <p className="text-xs text-slate-600 mt-1">
                            <MapPin className="w-3 h-3 inline mr-1" />
                            {asset.location}
                          </p>
                        )}
                        <div className="mt-2">
                          <Badge className={
                            asset.criticality_tier === 'tier_1' ? 'bg-red-100 text-red-700' :
                            asset.criticality_tier === 'tier_2' ? 'bg-amber-100 text-amber-700' :
                            'bg-emerald-100 text-emerald-700'
                          }>
                            {tierLabel}
                          </Badge>
                        </div>
                        {asset.rto_hours != null && (
                          <p className="text-xs mt-1">RTO: {asset.rto_hours}h</p>
                        )}
                        {asset.rpo_hours != null && (
                          <p className="text-xs mt-1">RPO: {asset.rpo_hours}h</p>
                        )}
                        {asset.owner && (
                          <p className="text-xs mt-1 text-slate-500">Owner: {asset.owner}</p>
                        )}
                        {asset.description && (
                          <p className="text-xs mt-2 text-slate-600 border-t pt-1">{asset.description}</p>
                        )}
                      </div>
                    </Popup>
                  </Marker>
                );
              })}
              
              {/* Location markers (without assets) */}
              {locations.filter(loc => 
                ((loc.coordinates?.lat && loc.coordinates?.lng) || (loc.latitude && loc.longitude)) &&
                !assetsWithCoords.some(a => a.location_id === loc.id)
              ).map((location) => (
                <Marker
                  key={`loc-${location.id}`}
                  position={[location.coordinates?.lat || location.latitude, location.coordinates?.lng || location.longitude]}
                  icon={L.divIcon({
                    className: 'custom-marker',
                    html: `<div style="background-color: ${LOCATION_TYPE_COLORS[location.type] || LOCATION_TYPE_COLORS.default}; width: 20px; height: 20px; border-radius: 50%; border: 2px solid white; box-shadow: 0 2px 5px rgba(0,0,0,0.2); opacity: 0.6;"></div>`,
                    iconSize: [20, 20],
                    iconAnchor: [10, 10],
                  })}
                >
                  <Tooltip direction="top" offset={[0, -12]} opacity={0.95}>
                    <div style={{ minWidth: '140px', padding: '2px' }}>
                      <div style={{ fontWeight: 600, fontSize: '13px' }}>{location.name}</div>
                      <div style={{ fontSize: '11px', color: '#64748b' }}>{location.type} — No assets linked</div>
                    </div>
                  </Tooltip>
                  <Popup>
                    <div>
                      <h4 className="font-semibold text-sm">{location.name}</h4>
                      <p className="text-xs text-slate-500">{location.type}</p>
                      <p className="text-xs text-slate-400 mt-1">No assets linked</p>
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          </div>
          
          {/* AI Analysis Panel */}
          {showAiPanel && aiAnalysis && (
            <div className="w-80 space-y-4">
              <Card className="bg-sky-50 border-sky-200">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-sky-700" />
                    AI Density Insights
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {/* Hotspots */}
                  {aiAnalysis.hotspots?.length > 0 && (
                    <div>
                      <h5 className="text-xs font-semibold text-slate-600 mb-2">Density Hotspots</h5>
                      {aiAnalysis.hotspots.map((hotspot, idx) => (
                        <div key={idx} className="p-2 bg-white rounded-lg mb-2">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium">{hotspot.name}</span>
                            <Badge variant={hotspot.risk_level === 'high' ? 'destructive' : 'secondary'}>
                              {hotspot.asset_count} assets
                            </Badge>
                          </div>
                          <p className="text-xs text-slate-500 mt-1">{hotspot.recommendation}</p>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Recommendations */}
                  {aiAnalysis.recommendations?.length > 0 && (
                    <div>
                      <h5 className="text-xs font-semibold text-slate-600 mb-2">Recommendations</h5>
                      <ul className="space-y-1">
                        {aiAnalysis.recommendations.map((rec, idx) => (
                          <li key={idx} className="text-xs text-slate-600 flex items-start gap-1">
                            <AlertTriangle className="w-3 h-3 text-amber-500 mt-0.5 flex-shrink-0" />
                            {rec}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </CardContent>
              </Card>
              
              {/* Legend */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-xs">Criticality Tier Legend</CardTitle>
                </CardHeader>
                <CardContent className="space-y-1">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded-full bg-red-500" />
                    <span className="text-xs">Criticality Tier 1 - Critical</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded-full bg-amber-500" />
                    <span className="text-xs">Criticality Tier 2 - Important</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded-full bg-emerald-500" />
                    <span className="text-xs">Criticality Tier 3 - Standard</span>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
        
        {/* Selected Region Stats */}
        {selectedRegion && filteredAssetIds.size > 0 && (
          <div className="mt-4 p-3 bg-sky-50 rounded-lg border border-sky-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Target className="w-4 h-4 text-sky-700" />
                <span className="text-sm font-medium text-indigo-800">
                  {filteredAssetIds.size} assets in selected region
                </span>
              </div>
              <div className="flex gap-2">
                <Badge variant="outline" className="bg-white">
                  {assets.filter(a => filteredAssetIds.has(a.id) && a.criticality_tier === 'tier_1').length} Critical
                </Badge>
                <Badge variant="outline" className="bg-white">
                  {assets.filter(a => filteredAssetIds.has(a.id) && a.criticality_tier === 'tier_2').length} Important
                </Badge>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
